import React, { Component, useState } from 'react';
import Icon from 'components/AppIcon';
import { logError } from './logError';

const isDev = import.meta.env?.DEV;

// ─── Report Issue Modal ────────────────────────────────────────────────────────
class ReportModal extends Component {
  constructor(props) {
    super(props);
    this.state = { description: '', submitting: false, submitted: false };
  }
  handleSubmit = async () => {
    this.setState({ submitting: true });
    await logError({
      error: this.props?.error,
      componentName: this.props?.componentName || 'ScreenErrorBoundary',
      actionName: 'user_reported_issue',
      context: { userDescription: this.state?.description, url: window.location?.href, timestamp: new Date()?.toISOString() },
    });
    this.setState({ submitting: false, submitted: true });
    setTimeout(() => this.props?.onClose(), 1800);
  };
  render() {
    const { onClose, error } = this.props;
    const { description, submitting, submitted } = this.state;
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={(e) => e?.target === e?.currentTarget && onClose()}>
        <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          {submitted ? (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <Icon name="CheckCircle" size={28} color="#10B981" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">Report Submitted</h3>
              <p className="text-sm text-muted-foreground">Thank you — our team has been notified.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-foreground">Report Issue</h3>
                <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors"><Icon name="X" size={16} color="var(--color-muted-foreground)" /></button>
              </div>
              <div className="rounded-lg p-3 mb-4 text-xs font-mono break-all" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: 'rgba(239,68,68,0.85)' }}>
                <p className="font-semibold mb-1" style={{ color: 'rgba(239,68,68,0.9)' }}>Error: {error?.message || 'Unknown error'}</p>
                {isDev && error?.stack && <p className="text-xs opacity-70 whitespace-pre-wrap line-clamp-4">{error?.stack?.slice(0, 400)}</p>}
              </div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Describe what you were doing (optional)</label>
              <textarea value={description} onChange={(e) => this.setState({ description: e?.target?.value })} placeholder="e.g. I clicked the trade button and the page crashed..." rows={3} className="w-full rounded-lg px-3 py-2 text-sm text-foreground resize-none focus:outline-none mb-4" style={{ background: 'var(--color-background)', border: '1px solid var(--color-border)' }} />
              <div className="flex gap-2">
                <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-medium" style={{ background: 'var(--color-background)', color: 'var(--color-muted-foreground)', border: '1px solid var(--color-border)' }}>Cancel</button>
                <button onClick={this.handleSubmit} disabled={submitting} className="flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2" style={{ background: 'rgba(0,217,255,0.15)', color: 'var(--color-primary)', border: '1px solid rgba(0,217,255,0.3)', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Sending...' : <><Icon name="Send" size={14} color="var(--color-primary)" /> Submit Report</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

// ─── ScreenErrorBoundary ───────────────────────────────────────────────────────
export class ScreenErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false, showReportModal: false };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error(`[ScreenErrorBoundary:${this.props?.name || 'Unknown'}]`, error, errorInfo);
    logError({
      error,
      componentName: this.props?.name || 'ScreenErrorBoundary',
      actionName: 'react_render_error',
      context: { componentStack: errorInfo?.componentStack?.slice(0, 2000) || '', url: window.location?.href, screenName: this.props?.name || '' },
    });
  }
  handleRetry = () => { window.location?.reload(); };
  render() {
    const { hasError, error, errorInfo, showDetails, showReportModal } = this.state;
    const { name } = this.props;
    if (!hasError) return this.props?.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--color-background)' }}>
        {showReportModal && <ReportModal error={error} componentName={name} onClose={() => this.setState({ showReportModal: false })} />}
        <div className="w-full max-w-lg">
          <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--color-card)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}>
              <Icon name="AlertTriangle" size={30} color="#EF4444" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-1">{name ? `The ${name} screen encountered an unexpected error.` : 'An unexpected error occurred.'}</p>
            <p className="text-xs text-muted-foreground mb-5">Your data is safe. You can retry or navigate to the Dashboard.</p>
            <div className="mb-5 text-left">
              <button onClick={() => this.setState({ showDetails: !showDetails })} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto">
                <Icon name={showDetails ? 'ChevronUp' : 'ChevronDown'} size={13} />
                {showDetails ? 'Hide' : 'Show'} error details
              </button>
              {showDetails && (
                <div className="mt-3 rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'rgba(239,68,68,0.9)' }}>{error?.message || 'Unknown error'}</p>
                  {isDev && error?.stack && <pre className="text-xs font-mono whitespace-pre-wrap break-all mt-2 opacity-70" style={{ color: 'rgba(239,68,68,0.75)', maxHeight: '160px', overflow: 'auto' }}>{error?.stack}</pre>}
                  {isDev && errorInfo?.componentStack && <pre className="text-xs font-mono whitespace-pre-wrap break-all mt-2 opacity-60" style={{ color: 'rgba(239,68,68,0.65)', maxHeight: '120px', overflow: 'auto' }}>{errorInfo?.componentStack}</pre>}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-2.5">
              <button onClick={this.handleRetry} className="w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'rgba(0,217,255,0.12)', color: 'var(--color-primary)', border: '1px solid rgba(0,217,255,0.25)' }}>
                <Icon name="RefreshCw" size={14} color="var(--color-primary)" /> Retry
              </button>
              <button onClick={() => { window.location.href = '/dashboard'; }} className="w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'var(--color-background)', color: 'var(--color-muted-foreground)', border: '1px solid var(--color-border)' }}>
                <Icon name="LayoutDashboard" size={14} /> Go to Dashboard
              </button>
              <button onClick={() => this.setState({ showReportModal: true })} className="w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                <Icon name="Flag" size={14} color="#EF4444" /> Report Issue
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">Error ID: {(name || 'unknown')?.toLowerCase()?.replace(/\s+/g, '-')}-{Date.now()?.toString(36)}</p>
        </div>
      </div>
    );
  }
}

export const ErrorFallback = ({ error, onRetry, screenName, customMessage }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
      >
        <Icon name="AlertTriangle" size={28} color="#EF4444" />
      </div>
      <h3 className="text-base font-semibold text-foreground mb-2">
        {customMessage || 'Unable to load data'}
      </h3>
      <p className="text-sm text-muted-foreground max-w-xs mb-4 leading-relaxed">
        Something went wrong while loading {screenName || 'this page'}. Please try again.
      </p>
      {error && (
        <div className="mb-4 w-full max-w-sm">
          <button
            onClick={() => setShowDetails((p) => !p)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto transition-colors"
          >
            <Icon name={showDetails ? 'ChevronUp' : 'ChevronDown'} size={12} />
            {showDetails ? 'Hide' : 'Show'} error details
          </button>
          {showDetails && (
            <div
              className="mt-2 p-3 rounded-lg text-left"
              style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}
            >
              <p className="text-xs font-mono break-all" style={{ color: 'rgba(239,68,68,0.8)' }}>
                {error?.message || String(error)}
              </p>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
            style={{ background: 'rgba(0,217,255,0.1)', color: 'var(--color-primary)', border: '1px solid rgba(0,217,255,0.2)' }}
          >
            <Icon name="RefreshCw" size={14} color="var(--color-primary)" />
            Retry
          </button>
        )}
        <button
          onClick={() => { window.location.href = '/dashboard'; }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{ background: 'var(--color-card)', color: 'var(--color-muted-foreground)', border: '1px solid var(--color-border)' }}
        >
          <Icon name="LayoutDashboard" size={14} />
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

class DataFetchErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[DataFetchErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName={this.props?.screenName} customMessage={this.props?.customMessage} />;
    return this.props?.children;
  }
}

export default DataFetchErrorBoundary;

export class DashboardErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[DashboardErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName="the Dashboard" customMessage="Unable to load dashboard data" />;
    return this.props?.children;
  }
}

export class PortfolioErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[PortfolioErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName="your Portfolio" customMessage="Unable to load portfolio data" />;
    return this.props?.children;
  }
}

export class StockExchangeErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[StockExchangeErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName="the Stock Exchange" customMessage="Unable to load market data" />;
    return this.props?.children;
  }
}

export class MarketplaceErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[MarketplaceErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName="the Marketplace" customMessage="Unable to load marketplace" />;
    return this.props?.children;
  }
}

export class BusinessesErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[BusinessesErrorBoundary]', error, info); }
  handleRetry = () => { this.setState({ hasError: false, error: null }); this.props?.onRetry?.(); };
  render() {
    if (this.state?.hasError)
      return <ErrorFallback error={this.state?.error} onRetry={this.handleRetry} screenName="Businesses" customMessage="Unable to load business data" />;
    return this.props?.children;
  }
}
